// src/helpers/rutaVentas.js
import axios from "axios";

const RAW_BASE_URL =
  process.env.REACT_APP_API_BASE || "https://backre-miami.us-3.evennode.com";

const BASE_URL = String(RAW_BASE_URL).replace(/\/+$/, "");

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

const p = (path) => `/${String(path || "").replace(/^\/+/, "")}`;

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("jwt") ||
  "";

const auth = () => {
  const token = getToken();
  return token ? { headers: { token } } : {};
};

const debug =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase() === "true";

const logUrl = (url, params, data, method = "GET") => {
  if (!debug) return;

  console.log("👉 METHOD:", method);
  console.log("👉 URL:", API.getUri({ url, params }));

  if (data) {
    console.log("📦 DATA ENVIADA:", data);
  }
};

async function req(method, url, { params, data, signal } = {}) {
  logUrl(url, params, data, method);

  try {
    const res = await API.request({
      method,
      url,
      params,
      data,
      signal,
      ...auth(),
    });

    return res.data;
  } catch (err) {
    if (debug) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      console.log("🧯 API ERROR:", msg);
      console.log("🧯 STATUS:", err?.response?.status);
      console.log("🧯 DATA:", err?.response?.data);
    }

    throw err;
  }
}

/* ----------------------------- NORMALIZADORES ---------------------------- */

const clean = (v) => String(v ?? "").trim();

const getId = (v) => {
  if (!v) return "";

  if (typeof v === "object") {
    return clean(v._id ?? v.id ?? v.value ?? "");
  }

  return clean(v);
};

const toNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const toNumberMinZero = (v, def = 0) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return def;
  return n < 0 ? 0 : n;
};

const toArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v === null || v === undefined || v === "") return [];
  return [v];
};

const toArrayIds = (v) => {
  return toArray(v).map(getId).filter(Boolean);
};

const unwrapVenta = (data) => {
  if (!data) return null;
  if (data?.venta) return data.venta;
  if (data?.data) return data.data;
  return data;
};

const unwrapVentas = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.ventas)) return data.ventas;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.docs)) return data.docs;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;

  return [];
};

const toFechaVenta = (v) => {
  if (!v) return new Date();

  const s = String(v).trim();

  // Si viene del input date: YYYY-MM-DD
  // Lo guardamos al mediodía UTC para que no caiga al día anterior en Argentina.
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (m) {
    const yyyy = Number(m[1]);
    const mm = Number(m[2]);
    const dd = Number(m[3]);

    return new Date(Date.UTC(yyyy, mm - 1, dd, 12, 0, 0));
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? new Date() : d;
};

export const normalizarItemVentaPayload = (item = {}) => {
  const src = item || {};

  return {
    producto: getId(src.producto),
    clienteRecibe: getId(src.clienteRecibe),

    cantidad: toNumberMinZero(src.cantidad, 1) || 1,

    precioVentaUSD: toNumberMinZero(src.precioVentaUSD, 0),

    costoEnvioUSD:
      src.costoEnvioUSD === "" || src.costoEnvioUSD === undefined
        ? undefined
        : toNumberMinZero(src.costoEnvioUSD, 0),

    coloresPreferidos: toArrayIds(src.coloresPreferidos),
    colorEntregado: getId(src.colorEntregado),

    observacionColor: clean(src.observacionColor).toUpperCase(),
    autorizacionPrecioTexto: clean(src.autorizacionPrecioTexto).toUpperCase(),

    aclaracion: clean(src.aclaracion).toUpperCase(),
    estadoItem: clean(src.estadoItem || "PENDIENTE").toUpperCase(),
    observaciones: clean(src.observaciones).toUpperCase(),
  };
};

export const normalizarVentaPayload = (payload = {}) => {
  const src = payload || {};

  return {
    nroventa: src.nroventa ? Number(src.nroventa) : undefined,
    fecha: src.fecha || undefined,

    clienteCompra: getId(src.clienteCompra),
    vendedor: getId(src.vendedor),
    colaboradorVenta: getId(src.colaboradorVenta),

    /*
      Se conserva undefined cuando el formulario no envía este campo.
      Así una edición antigua no elimina accidentalmente el tax
      de una venta nueva.
    */
taxAplicado:
  src.taxAplicado === undefined
    ? undefined
    : src.taxAplicado === true ||
      String(src.taxAplicado || "").toLowerCase() === "true",

      taxPorcentaje:
        src.taxPorcentaje === undefined ||
        src.taxPorcentaje === null ||
        src.taxPorcentaje === ""
          ? undefined
          : toNumberMinZero(src.taxPorcentaje, 0),

      taxBaseUSD:
        src.taxBaseUSD === undefined ||
        src.taxBaseUSD === null ||
        src.taxBaseUSD === ""
          ? undefined
          : toNumberMinZero(src.taxBaseUSD, 0),

      taxMontoUSD:
        src.taxMontoUSD === undefined ||
        src.taxMontoUSD === null ||
        src.taxMontoUSD === ""
          ? undefined
          : toNumberMinZero(src.taxMontoUSD, 0),

      items: Array.isArray(src.items)
      ? src.items.map((item) => normalizarItemVentaPayload(item))
      : [],

    observaciones: clean(src.observaciones).toUpperCase(),

    estado: clean(
      src.estado || "PENDIENTE"
    ).toUpperCase(),

    estadoRecepcion:
      src.estadoRecepcion === undefined ||
      src.estadoRecepcion === null ||
      src.estadoRecepcion === ""
        ? undefined
        : clean(src.estadoRecepcion).toUpperCase(),

    activo: src.activo ?? true,
  };
};

/* --------------------------------- VENTAS -------------------------------- */

export async function getVentas({
  page = 1,
  limit = 50,
  q = "",
  clienteCompra = "",
  clienteRecibe = "",
  vendedor = "",
  estado = "",
  desde = "",
  hasta = "",
  signal,
} = {}) {
  const params = {
    page,
    limit,
    q: String(q || "").trim(),
  };

  if (clienteCompra) params.clienteCompra = clienteCompra;
  if (clienteRecibe) params.clienteRecibe = clienteRecibe;
  if (vendedor) params.vendedor = vendedor;
  if (estado) params.estado = estado;
  if (desde) params.desde = desde;
  if (hasta) params.hasta = hasta;

  const data = await req("GET", p("ventas"), {
    params,
    signal,
  });

  const ventas = unwrapVentas(data);

  return {
    ventas,
    total: Number(data?.total ?? ventas.length ?? 0),
    page: Number(data?.page ?? page),
    limit: Number(data?.limit ?? limit),
  };
}

export async function getVentaById(id, { signal } = {}) {
  const data = await req("GET", p(`ventas/${id}`), { signal });
  return unwrapVenta(data);
}

export async function getVentaByCodigo(nroventa, { signal } = {}) {
  const codigo = String(nroventa || "").trim();

  if (!codigo) return null;

  const data = await req("GET", p(`ventas/codigo/${codigo}`), { signal });

  return unwrapVenta(data);
}

export async function postVenta(payload, { signal } = {}) {
  const data = normalizarVentaPayload(payload);

  if (debug) {
    console.log("🟢 postVenta PAYLOAD:", data);
  }

  return req("POST", p("ventas"), {
    data,
    signal,
  });
}

export async function putVenta(id, payload, { signal } = {}) {
  const data = normalizarVentaPayload(payload);

  if (debug) {
    console.log("🟣 putVenta ID:", id);
    console.log("🟣 putVenta ORIGINAL:", payload);
    console.log("🟢 putVenta NORMALIZADA:", data);
    console.log("🌐 putVenta URL:", `${BASE_URL}${p(`ventas/${id}`)}`);
  }

  return req("PUT", p(`ventas/${id}`), {
    data,
    signal,
  });
}

export async function deleteVenta(id, { signal } = {}) {
  return req("DELETE", p(`ventas/${id}`), { signal });
}

export async function anularVenta(id, observaciones = "", { signal } = {}) {
  return req("PUT", p(`ventas/anular/${id}`), {
    data: {
      observaciones: clean(observaciones).toUpperCase(),
    },
    signal,
  });
}

export async function cambiarEstadoVenta(id, estado, { signal } = {}) {
  return req("PATCH", p(`ventas/${id}/estado`), {
    data: {
      estado: clean(estado).toUpperCase(),
    },
    signal,
  });
}

export async function completarRecepcionVenta(
  id,
  asignaciones = [],
  { signal } = {}
) {
  const data = {
    asignaciones: Array.isArray(asignaciones)
      ? asignaciones
          .map((asignacion) => ({
            itemId: getId(
              asignacion?.itemId
            ),

            clienteRecibe: getId(
              asignacion?.clienteRecibe
            ),
          }))
          .filter(
            (asignacion) =>
              asignacion.itemId &&
              asignacion.clienteRecibe
          )
      : [],
  };

  if (debug) {
    console.log(
      "🟠 completarRecepcionVenta:",
      id,
      data
    );
  }

  return req(
    "PUT",
    p(`ventas/${id}/completar-recepcion`),
    {
      data,
      signal,
    }
  );
}

export async function marcarWhatsappVenta(
  id,
  { telefonoDestino = "", signal } = {}
) {
  return req("PUT", p(`ventas/${id}/whatsapp-aviso`), {
    data: {
      telefonoDestino: clean(telefonoDestino),
    },
    signal,
  });
}

// Compatible con:
// buscarVentas({ q: "1", limit: 20 })
// buscarVentas("1", 20)
export async function buscarVentas(arg1 = {}, arg2 = 20) {
  let q = "";
  let limit = 20;
  let signal;

  if (typeof arg1 === "string" || typeof arg1 === "number") {
    q = String(arg1 || "").trim();
    limit = Number(arg2 || 20);
  } else {
    q = String(arg1?.q || "").trim();
    limit = Number(arg1?.limit || 20);
    signal = arg1?.signal;
  }

  if (!q || q.length < 1) return [];

  const data = await req("GET", p("ventas/buscar"), {
    params: { q, limit },
    signal,
  });

  const ventas = unwrapVentas(data);

  if (debug) {
    console.log("🔎 buscarVentas q:", q);
    console.log("🔎 ventas encontradas:", ventas);
  }

  return ventas;
}

export async function getReporteVentas({
  q = "",
  clienteCompra = "",
  clienteRecibe = "",
  vendedor = "",
  estado = "",
  estadoItem = "",
  categoria = "",
  desde = "",
  hasta = "",
  incluirAnuladas = false,
  signal,
} = {}) {
  const params = {
    q: String(q || "").trim(),
  };

  if (clienteCompra) params.clienteCompra = clienteCompra;
  if (clienteRecibe) params.clienteRecibe = clienteRecibe;
  if (vendedor) params.vendedor = vendedor;
  if (estado) params.estado = estado;
  if (estadoItem) params.estadoItem = estadoItem;
  if (categoria) params.categoria = categoria;
  if (desde) params.desde = desde;
  if (hasta) params.hasta = hasta;
  if (incluirAnuladas) params.incluirAnuladas = "true";

  return req("GET", p("ventas/reporte"), {
    params,
    signal,
  });
}

export async function putVentaDatosGenerales(id, payload = {}, { signal } = {}) {
  const data = {};

  if (payload.fecha !== undefined) {
    data.fecha = payload.fecha || undefined;
  }

  if (payload.estado !== undefined) {
    data.estado = clean(payload.estado || "PENDIENTE").toUpperCase();
  }

  if (payload.observaciones !== undefined) {
    data.observaciones = clean(payload.observaciones).toUpperCase();
  }

  return req("PUT", p(`ventas/${id}`), {
    data,
    signal,
  });
}

export const subirComprobanteVenta = async (ventaId, tipo, file) => {
  const formData = new FormData();
  formData.append("archivo", file);

  const token = getToken();

  const resp = await API.post(
    `/ventas/${ventaId}/comprobantes/${tipo}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        token,
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    }
  );

  return resp.data;
};

export const eliminarComprobanteVenta = async (ventaId, tipo) => {
  const token = getToken();

  const resp = await API.delete(`/ventas/${ventaId}/comprobantes/${tipo}`, {
    headers: {
      token,
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });

  return resp.data;
};

export const getComprobantesVenta = async (ventaId) => {
  const token = getToken();

  const resp = await API.get(`/ventas/${ventaId}/comprobantes`, {
    headers: {
      token,
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });

  return resp.data;
};

export async function getRentabilidadVentas({
  desde = "",
  hasta = "",
  sede = "TODAS",
  signal,
} = {}) {
  const params = {};

  if (desde) params.desde = desde;
  if (hasta) params.hasta = hasta;

  params.sede = String(
    sede || "TODAS"
  )
    .trim()
    .toUpperCase();

  return req(
    "GET",
    p("ventas/rentabilidad"),
    {
      params,
      signal,
    }
  );
}

/* --------------------------- DASHBOARD HOME --------------------------- */

export async function getDashboardHomeVentas({
  periodo = "SEMANA",
  signal,
} = {}) {
  const periodoNormalizado = String(
    periodo || "SEMANA"
  )
    .trim()
    .toUpperCase();

  const periodosValidos = [
    "HOY",
    "SEMANA",
    "MES",
  ];

  const periodoFinal =
    periodosValidos.includes(
      periodoNormalizado
    )
      ? periodoNormalizado
      : "SEMANA";

  const data = await req(
    "GET",
    p("ventas/dashboard-home"),
    {
      params: {
        periodo: periodoFinal,
      },
      signal,
    }
  );

  /*
    Normalizamos la respuesta para que el Home
    nunca tenga que defenderse de undefined.
  */
  return {
    ok:
      data?.ok !== false,

    alcance: {
      tipo:
        data?.alcance?.tipo ||
        "VENDEDOR",

      role:
        data?.alcance?.role ||
        "",

      sede:
        data?.alcance?.sede ||
        "",

      usuarioId:
        data?.alcance?.usuarioId ||
        null,
    },

    periodo: {
      tipo:
        data?.periodo?.tipo ||
        periodoFinal,

      desde:
        data?.periodo?.desde ||
        null,

      hasta:
        data?.periodo?.hasta ||
        null,

      cantidadDias:
        toNumber(
          data?.periodo
            ?.cantidadDias,
          0
        ),
    },

    resumen: {
      ventas:
        toNumber(
          data?.resumen?.ventas,
          0
        ),

      montoVendidoUSD:
        toNumber(
          data?.resumen
            ?.montoVendidoUSD,
          0
        ),

      saldoPendienteUSD:
        toNumber(
          data?.resumen
            ?.saldoPendienteUSD,
          0
        ),

      incompletas:
        toNumber(
          data?.resumen
            ?.incompletas,
          0
        ),

      variacionVentasPct:
        data?.resumen
          ?.variacionVentasPct ??
        null,

      variacionMontoPct:
        data?.resumen
          ?.variacionMontoPct ??
        null,

      variacionSaldoPct:
        data?.resumen
          ?.variacionSaldoPct ??
        null,

      variacionIncompletasPct:
        data?.resumen
          ?.variacionIncompletasPct ??
        null,
    },

    alertas: {
      faltanDatosCuba:
        toNumber(
          data?.alertas
            ?.faltanDatosCuba,
          0
        ),

      pagosPorAcreditar:
        toNumber(
          data?.alertas
            ?.pagosPorAcreditar,
          0
        ),

      ventasParciales:
        toNumber(
          data?.alertas
            ?.ventasParciales,
          0
        ),

      seguimientosPendientes:
        toNumber(
          data?.alertas
            ?.seguimientosPendientes,
          0
        ),

      seguimientosPendientesRegistros:
        toNumber(
          data?.alertas
            ?.seguimientosPendientesRegistros,
          0
        ),
    },

    rendimiento:
      Array.isArray(
        data?.rendimiento
      )
        ? data.rendimiento
        : [],

    resumenSedes:
      Array.isArray(
        data?.resumenSedes
      )
        ? data.resumenSedes
        : [],

    resumenVendedores:
      Array.isArray(
        data?.resumenVendedores
      )
        ? data.resumenVendedores
        : [],

    ultimasVentas:
      Array.isArray(
        data?.ultimasVentas
      )
        ? data.ultimasVentas
        : [],
  };
}